import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  fetchCancelInfo,
  cancelBooking,
  type CancelInfo,
} from "../lib/api.ts";

export default function Cancel() {
  const { slug, token } = useParams<{ slug: string; token: string }>();

  const [info, setInfo] = useState<CancelInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(false);

  useEffect(() => {
    if (!slug || !token) return;
    fetchCancelInfo(slug, token)
      .then(setInfo)
      .catch(() => setError("Could not load appointment information."))
      .finally(() => setLoading(false));
  }, [slug, token]);

  async function handleCancel() {
    if (!slug || !token) return;
    setCancelling(true);
    setError(null);
    try {
      await cancelBooking(slug, token);
      setCancelled(true);
    } catch {
      setError("Failed to cancel the appointment. Please try again.");
    } finally {
      setCancelling(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500 text-sm animate-pulse">Loading...</div>
      </div>
    );
  }

  if (error && !info) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <p className="text-red-600 text-center">{error}</p>
      </div>
    );
  }

  if (!info) return null;

  const formattedDate = new Date(info.starts_at).toLocaleString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">
            {cancelled ? "Appointment Cancelled" : "Cancel Appointment"}
          </h1>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
          <div className="p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
              Service
            </p>
            <p className="font-medium text-gray-900">{info.service_name}</p>
          </div>
          <div className="p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
              Date &amp; Time
            </p>
            <p className="font-medium text-gray-900">{formattedDate}</p>
            <p className="text-sm text-gray-500 mt-0.5">with {info.staff_name}</p>
          </div>
          <div className="p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
              Status
            </p>
            <p className="font-medium text-gray-900 capitalize">{info.status}</p>
          </div>
        </div>

        {error && (
          <p className="mt-4 text-red-600 text-sm text-center">{error}</p>
        )}

        <div className="mt-6 flex flex-col gap-3">
          {cancelled ? (
            <>
              <p className="text-center text-gray-500 text-sm">
                Your appointment has been cancelled successfully.
              </p>
              <Link
                to={`/${slug}`}
                className="block text-center py-2.5 px-4 rounded-lg btn-accent text-sm font-medium"
              >
                Book a new appointment
              </Link>
            </>
          ) : info.can_cancel ? (
            <>
              <p className="text-center text-gray-500 text-sm">
                Are you sure you want to cancel this appointment?
              </p>
              <button
                type="button"
                onClick={handleCancel}
                disabled={cancelling}
                className="w-full py-2.5 px-4 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-60 transition-all"
              >
                {cancelling ? "Cancelling..." : "Cancel Appointment"}
              </button>
              <Link
                to={`/${slug}`}
                className="block text-center py-2.5 px-4 rounded-lg border border-gray-300 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Keep my appointment
              </Link>
            </>
          ) : (
            <>
              <p className="text-center text-gray-500 text-sm">
                This appointment can no longer be cancelled online. Please contact us
                directly.
              </p>
              <Link
                to={`/${slug}`}
                className="block text-center py-2.5 px-4 rounded-lg btn-accent text-sm font-medium"
              >
                Back to homepage
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
